import React from 'react'
import { Text, TextProps as TextPropsOriginal } from 'rebass'
import styled, {
  createGlobalStyle,
  css,
  DefaultTheme,
  ThemeProvider as StyledComponentsThemeProvider,
} from 'styled-components'

import { colors as ColorsPalette, colorsDark } from './colors'
import { cssStringFromTheme } from './css/cssStringFromTheme'
import { darkTheme } from './darkTheme'
import { Colors, ThemeColors } from './styled'
import typography from './typography'

export * from './components'

type TextProps = Omit<TextPropsOriginal, 'css'>

export const MEDIA_WIDTHS = {
  upToExtraSmall: 500,
  upToSmall: 720,
  upToMedium: 960,
  upToMediumLarge: 1080,
  upToLarge: 1280,
  upToExtraLarge: 1440,
}

const DEVICES = {
  mobileS: '320px',
  mobileM: '375px',
  mobileL: '425px',
  tablet: '768px',
  laptop: '1024px',
  laptopL: '1440px',
  desktop: '2560px',
}

// Migrating to a standard z-index system https://getbootstrap.com/docs/5.0/layout/z-index/
// Please avoid using deprecated numbers
export enum Z_INDEX {
  deprecated_zero = 0,
  deprecated_content = 1,
  dropdown = 1000,
  sticky = 1020,
  fixed = 1030,
  modalBackdrop = 1040,
  offcanvas = 1050,
  modal = 1060,
  popover = 1070,
  tooltip = 1080,
}

const mediaWidthTemplates: { [width in keyof typeof MEDIA_WIDTHS]: typeof css } = Object.keys(MEDIA_WIDTHS).reduce(
  (accumulator, size) => {
    ;(accumulator as any)[size] = (a: any, b: any, c: any) => css`
      @media (max-width: ${(MEDIA_WIDTHS as any)[size]}px) {
        ${css(a, b, c)}
      }
    `
    return accumulator
  },
  {}
) as any

const deprecated_white = ColorsPalette.white
const deprecated_black = ColorsPalette.black

function kodiakThemeColors(): ThemeColors {
  return {
    userThemeColor: colorsDark.userThemeColor,

    backgroundBackdrop: colorsDark.backgroundBackdrop,
    backgroundSurface: colorsDark.backgroundSurface,
    backgroundModule: colorsDark.backgroundModule,
    backgroundInteractive: colorsDark.backgroundInteractive,
    backgroundOutline: colorsDark.backgroundOutline,
    backgroundScrim: colorsDark.backgroundScrim,

    borderSecondary: colorsDark.borderSecondary,

    textPrimary: colorsDark.textPrimary,
    textSecondary: colorsDark.textSecondary,
    textTertiary: colorsDark.textTertiary,
    textAdditional: colorsDark.textAdditional,

    accentAction: colorsDark.accentAction,
    accentActive: colorsDark.accentActive,
    accentSuccess: colorsDark.accentSuccess,
    accentWarning: colorsDark.accentWarning,
    accentFailure: colorsDark.accentFailure,

    accentActionSoft: colorsDark.accentActionSoft,
    accentActiveSoft: colorsDark.accentActiveSoft,
    accentSuccessSoft: colorsDark.accentSuccessSoft,
    accentWarningSoft: colorsDark.accentWarningSoft,
    accentFailureSoft: colorsDark.accentFailureSoft,

    accentTextDarkPrimary: colorsDark.accentTextDarkPrimary,
    accentTextDarkSecondary: colorsDark.accentTextDarkSecondary,
    accentTextDarkTertiary: colorsDark.accentTextDarkTertiary,

    accentTextLightPrimary: colorsDark.accentTextLightPrimary,
    accentTextLightSecondary: colorsDark.accentTextLightSecondary,
    accentTextLightTertiary: colorsDark.accentTextLightTertiary,

    primaryGradient: colorsDark.primaryGradient,
    chartGradient: colorsDark.chartGradient,
    chartGradientHover: colorsDark.chartGradientHover,

    warning500: colorsDark.warning500,
    warning700: colorsDark.warning700,
    error500: colorsDark.error500,
    error700: colorsDark.error700,
    success500: colorsDark.success500,
    success700: colorsDark.success700,

    hoverState: colorsDark.hoverState,
    hoverDefault: colorsDark.hoverDefault,

    cardPrimary: colorsDark.cardPrimary,
    cardSecondary: colorsDark.cardSecondary,
    cardTertiary: colorsDark.cardTertiary,
    cardBorderPrimary: colorsDark.cardBorderPrimary,
    cardBorderSecondary: colorsDark.cardBorderSecondary,

    linePrimary: colorsDark.linePrimary,

    buttonPrimary: colorsDark.buttonPrimary,
    buttonSecondary: colorsDark.buttonSecondary,
    buttonSubtle1: colorsDark.buttonSubtle1,
    buttonSubtle2: colorsDark.buttonSubtle2,
    buttonBorder: colorsDark.buttonBorder,
    buttonDisable: colorsDark.buttonDisable,

    stateOverlayHover: colorsDark.stateOverlayHover,
    stateOverlayPressed: colorsDark.stateOverlayPressed,

    none: colorsDark.none,
    white: ColorsPalette.white,
    black: ColorsPalette.black,

    // chain colors are same for light/dark mode
    chain_1: colorsDark.chain_1,
    chain_3: colorsDark.chain_3,
    chain_4: colorsDark.chain_4,
    chain_5: colorsDark.chain_5,
    chain_10: colorsDark.chain_10,
    chain_137: colorsDark.chain_137,
    chain_42: colorsDark.chain_42,
    chain_69: colorsDark.chain_69,
    chain_42161: colorsDark.chain_42161,
    chain_421611: colorsDark.chain_421611,
    chain_80001: colorsDark.chain_80001,

    blue200: ColorsPalette.blue200,
    flyoutDropShadow:
      '0px 24px 32px rgba(0, 0, 0, 0.04), 0px 16px 24px rgba(0, 0, 0, 0.04), 0px 4px 8px rgba(0, 0, 0, 0.04), 0px 0px 1px rgba(0, 0, 0, 0.12)',
  }
}

function oldColors(): Colors {
  return {
    // base
    deprecated_white,
    deprecated_black,

    // text
    deprecated_text1: '#FFFFFF',
    deprecated_text2: '#C3C5CB',
    deprecated_text3: '#8F96AC',
    deprecated_text4: '#B2B9D2',
    deprecated_text5: '#2C2F36',

    // backgrounds / greys
    deprecated_bg0: '#191B1F',
    deprecated_bg1: '#212429',
    deprecated_bg2: '#2C2F36',
    deprecated_bg3: '#40444F',
    deprecated_bg4: '#565A69',
    deprecated_bg5: '#6C7284',
    deprecated_bg6: '#1A2028',

    //specialty colors
    deprecated_modalBG: 'rgba(0,0,0,.425)',
    deprecated_advancedBG: 'rgba(0,0,0,0.1)',

    //primary colors
    deprecated_primary1: '#2172E5',
    deprecated_primary2: '#3680E7',
    deprecated_primary3: '#4D8FEA',
    deprecated_primary4: '#376bad70',
    deprecated_primary5: '#153d6f70',

    // color text
    deprecated_primaryText1: '#5090ea',

    // secondary colors
    deprecated_secondary1: '#2172E5',
    deprecated_secondary2: '#17000b26',
    deprecated_secondary3: '#17000b26',

    // other
    deprecated_red1: '#FF4343',
    deprecated_red2: '#F82D3A',
    deprecated_red3: '#D60000',
    deprecated_green1: '#27AE60',
    deprecated_yellow1: '#E3A507',
    deprecated_yellow2: '#FF8F00',
    deprecated_yellow3: '#F3B71E',
    deprecated_blue1: '#2172E5',
    deprecated_blue2: '#5199FF',
    deprecated_error: '#FD4040',
    deprecated_success: '#27AE60',
    deprecated_warning: '#FF8F00',

    // dont wanna forget these blue yet
    deprecated_blue4: '#153d6f70',
  }
}

function getTheme(): DefaultTheme {
  const useColors = oldColors()
  return {
    ...kodiakThemeColors(),
    ...useColors,

    grids: {
      sm: 8,
      md: 12,
      lg: 24,
    },

    //shadows
    shadow1: '#000',

    // media queries
    mediaWidth: mediaWidthTemplates,

    // css snippets
    flexColumnNoWrap: css`
      display: flex;
      flex-flow: column nowrap;
    `,
    flexRowNoWrap: css`
      display: flex;
      flex-flow: row nowrap;
    `,

    typography,
    devices: DEVICES,
  }
}

export default function ThemeProvider({ children }: { children: React.ReactNode }) {
  const themeObject: DefaultTheme = getTheme()
  return <StyledComponentsThemeProvider theme={themeObject}>{children}</StyledComponentsThemeProvider>
}

const TextWrapper = styled(Text)<{ color: keyof Colors }>`
  color: ${({ color, theme }) => (theme as any)[color]};
`

/**
 * Preset styles of the Rebass Text component
 */
export const ThemedText = {
  DeprecatedMain(props: TextProps) {
    return <TextWrapper fontWeight={500} color="deprecated_text2" {...props} />
  },
  DeprecatedLink(props: TextProps) {
    return <TextWrapper fontWeight={500} color="deprecated_primary1" {...props} />
  },
  DeprecatedLabel(props: TextProps) {
    return <TextWrapper fontWeight={600} color="deprecated_text1" {...props} />
  },
  DeprecatedBlack(props: TextProps) {
    return <TextWrapper fontWeight={500} color="deprecated_text1" {...props} />
  },
  DeprecatedWhite(props: TextProps) {
    return <TextWrapper fontWeight={500} color="deprecated_white" {...props} />
  },
  DeprecatedBody(props: TextProps) {
    return <TextWrapper fontWeight={400} fontSize={16} color="deprecated_text1" {...props} />
  },
  DeprecatedLargeHeader(props: TextProps) {
    return <TextWrapper fontWeight={600} fontSize={24} {...props} />
  },
  DeprecatedMediumHeader(props: TextProps) {
    return <TextWrapper fontWeight={500} fontSize={20} {...props} />
  },
  DeprecatedSubHeader(props: TextProps) {
    return <TextWrapper fontWeight={400} fontSize={14} {...props} />
  },
  DeprecatedSmall(props: TextProps) {
    return <TextWrapper fontWeight={500} fontSize={11} {...props} />
  },
  DeprecatedBlue(props: TextProps) {
    return <TextWrapper fontWeight={500} color="deprecated_blue1" {...props} />
  },
  DeprecatedYellow(props: TextProps) {
    return <TextWrapper fontWeight={500} color="deprecated_yellow3" {...props} />
  },
  DeprecatedDarkGray(props: TextProps) {
    return <TextWrapper fontWeight={500} color="deprecated_text3" {...props} />
  },
  DeprecatedGray(props: TextProps) {
    return <TextWrapper fontWeight={500} color="deprecated_bg3" {...props} />
  },
  DeprecatedItalic(props: TextProps) {
    return <TextWrapper fontWeight={500} fontSize={12} fontStyle="italic" color="deprecated_text2" {...props} />
  },
  DeprecatedError({ error, ...props }: { error: boolean } & TextProps) {
    return <TextWrapper fontWeight={500} color={error ? 'deprecated_red1' : 'deprecated_text2'} {...props} />
  },
}

export const ThemedGlobalStyle = createGlobalStyle`
html {
  color: ${({ theme }) => theme.textPrimary};
  background-color: ${({ theme }) => theme.deprecated_bg1} !important;
}

body {
  overscroll-behavior-x: none; // prevent sideways overscroll. Might cause issues later
  overscroll-behavior: none;

  --sb-track-color: ${({ theme }) => theme.deprecated_bg1};
    --sb-thumb-color: ${({ theme }) => theme.buttonSubtle1};
    --sb-size: 6px;

    scrollbar-color: var(--sb-thumb-color)
                    var(--sb-track-color);

  /* not globalized until the page layouts are properly fixed */
  ::-webkit-scrollbar {
    width: var(--sb-size);
    transition: all 1s linear;
  }

  ::-webkit-scrollbar-track {
    background: opacify(0.1, var(--sb-track-color));
    border-radius: 8px;
    box-shadow: inset 0 0 6px rgba(0, 0, 0, .3);
  }

  ::-webkit-scrollbar-thumb {
    background: ${({ theme }) => theme.buttonPrimary};
    border-radius: 8px;
  }

  .logo-svg-container svg {
  width: 100% !important;
  height: 100% !important;
  max-width: 100%;
  max-height: 100%;
  object-fit: contain;
}
}

a {
 color: ${({ theme }) => theme.deprecated_blue1};
}

:root {
  ${cssStringFromTheme(darkTheme)}
}
`

export const HoverText = styled(ThemedText.DeprecatedMain)`
  text-decoration: none;
  color: ${({ theme }) => theme.textSecondary};
  :hover {
    color: ${({ theme }) => theme.textPrimary};
    text-decoration: none;
  }
`
